import { DateRange } from "@mui/x-date-pickers-pro";
import { DateTime, WeekdayNumbers } from "luxon";
import { i18n as I18n, TFunction } from "i18next";

export const DATE_PATH_FORMAT = "yyyy-MM-dd";

export const DEFAULT_TIMEZONE = "America/Los_Angeles";

// luxon toLocaleString presets
export const DATETIME_EXCEL = "MM/dd/yyyy hh:mm a";
export const DATETIME_SHORT_WITHOUT_YEAR: Intl.DateTimeFormatOptions = {
  ...DateTime.DATETIME_SHORT,
  year: undefined,
};

export const formatDateOffset = (t: TFunction, offset: number): string => {
  if (offset === 0) {
    return t("components.DateRangePicker.today");
  }
  if (offset === 1) {
    return t("components.DateRangePicker.tomorrow");
  }
  if (offset === -1) {
    return t("components.DateRangePicker.yesterday");
  }
  if (offset < -1) {
    return t("components.DateRangePicker.minusDays", {
      days: Math.abs(offset),
    });
  }
  if (offset > 1) {
    return t("components.DateRangePicker.plusDays", { days: offset });
  }
  return t("components.DateRangePicker.invalid");
};

export const formatCompactDate = (
  t: TFunction,
  i18n: I18n,
  input?: DateTime | number,
  includeToday?: boolean
): string => {
  let date: DateTime | undefined;
  if (typeof input === "number") {
    date = DateTime.fromMillis(input);
  } else if (input instanceof DateTime) {
    date = input;
  }

  if (!date || !date.isValid) {
    return t("components.DateRangePicker.invalid");
  }

  const resultSet: string[] = [];

  if (isToday(date)) {
    if (includeToday) {
      resultSet.push(t("components.DateRangePicker.today"));
    }
  } else {
    if (isThisWeek(date)) {
      resultSet.push(
        date.toLocaleString({ weekday: "long" }, { locale: i18n.language })
      );
    } else {
      resultSet.push(
        date.toLocaleString(
          { month: "short", day: "numeric" },
          { locale: i18n.language }
        )
      );
    }

    if (!isThisYear(date)) {
      resultSet.push(
        date.toLocaleString({ year: "numeric" }, { locale: i18n.language })
      );
    }
  }
  return resultSet.join(" ");
};

export const isToday = (date: DateTime): boolean =>
  isSameDate(date, DateTime.local());

export const isSameDate = (a: DateTime, b: DateTime): boolean =>
  a.toISODate() === b.toISODate();

export const isThisWeek = (date: DateTime): boolean =>
  date >
  DateTime.local()
    .minus({ days: 6 })
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

export const isThisMonth = (date: DateTime): boolean =>
  date >
  DateTime.local().set({
    day: 1,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });

export const isThisYear = (date: DateTime): boolean =>
  date.year === DateTime.local().year;

export const isSameRange = (
  a: DateRange<DateTime>,
  b: DateRange<DateTime>
): boolean => {
  const [aStart, aEnd] = a;
  const [bStart, bEnd] = b;
  if (!Array.isArray(a) || !Array.isArray(b)) {
    return a === b;
  }
  let startIsSame = false;
  startIsSame = !aStart || !bStart ? aStart === bStart : aStart.equals(bStart);
  let endIsSame = false;
  endIsSame = !aEnd || !bEnd ? aEnd === bEnd : aEnd.equals(bEnd);
  return startIsSame && endIsSame;
};

export const roundToMinute = (date: DateTime): DateTime =>
  DateTime.fromMillis(date.toMillis()).set({
    minute: date.second >= 30 ? date.minute + 1 : date.minute,
    second: 0,
    millisecond: 0,
  });

export const snapToDayStart = (date: DateTime): DateTime =>
  DateTime.fromMillis(date.toMillis()).set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });

export const snapToDayEnd = (date: DateTime): DateTime =>
  snapToDayStart(date).plus({ days: 1 }).minus({ milliseconds: 1 });

export const isSameDay = (date: DateTime): boolean =>
  date.hasSame(DateTime.now(), "day");

export const getLastCheckedString = ({
  lastCheckedSeconds,
  staticPollThreshold,
  staticPollUpperThreshold,
  t,
}: {
  lastCheckedSeconds?: number;
  staticPollThreshold: number;
  staticPollUpperThreshold: number;
  t: TFunction;
}): string => {
  if (!lastCheckedSeconds) {
    return t("components.robots.RobotSummary.stale");
  }
  if (lastCheckedSeconds <= staticPollThreshold) {
    return `${t("views.autotractor.lastPolled")} < ${staticPollThreshold}${t(
      "utils.units.s"
    )}`;
  }
  if (lastCheckedSeconds >= staticPollUpperThreshold * 60) {
    return `${t(
      "views.autotractor.lastPolled"
    )} > ${staticPollUpperThreshold}${t("utils.units.min")}`;
  }
  return `${t("views.autotractor.lastPolled")} ${lastCheckedSeconds.toFixed(
    0
  )}${t("utils.units.s")}`;
};

export const getLastUpdatedString = ({
  lastUpdatedSeconds,
  lastUpdatedThreshold,
  lastUpdatedUpperThreshold,
  t,
}: {
  lastUpdatedSeconds: number;
  lastUpdatedThreshold: number;
  lastUpdatedUpperThreshold: number;
  t: TFunction;
}): string => {
  if (lastUpdatedSeconds < lastUpdatedThreshold) {
    return `${t(
      "models.robots.fields.reportedAt"
    )} < ${lastUpdatedThreshold}${t("utils.units.s")}`;
  }
  if (lastUpdatedSeconds >= lastUpdatedUpperThreshold * 60) {
    return `${t(
      "models.robots.fields.reportedAt"
    )} > ${lastUpdatedUpperThreshold}${t("utils.units.min")}`;
  }
  return `${t("models.robots.fields.reportedAt")} ${lastUpdatedSeconds.toFixed(
    0
  )}${t("utils.units.s")}`;
};

export const carbonWeekdayToISOWeekday: (weekday: number) => WeekdayNumbers = (
  weekday
) => {
  // internally, our weekdays run from 0 (sunday) to 6 (saturday); luxon and ISO
  // weekdays run from 1 (monday) to 7 (sunday).  luxon just does weekday math
  // modulo 7 so this doesn't cause a functional bug, but both because
  // @types/luxon 3.4+ has strict typing for weekday parameters and to prevent
  // regressions in the future, actually limit our weekdays to the expected
  // range and decorate them with the luxon type.
  switch (weekday % 7) {
    case 0: {
      return 7;
    }
    default: {
      return (weekday % 7) as WeekdayNumbers;
    }
  }
};
